import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Phone, Send } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { PAGE } from "@/lib/pageContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { lang, t } = useI18n();
  const p = pickLang(PAGE, lang);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Message sent!");
      setSent(true);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative overflow-hidden bg-radial-glow">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <div className="overline">{p.contact_overline}</div>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] text-white">
              {p.contact_title_a} <span className="gradient-text">{p.contact_title_b}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-200 leading-relaxed">{p.contact_subtitle}</p>

            <div className="mt-10 md:mt-12 space-y-5 md:space-y-6">
              <div className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-xl glass flex items-center justify-center shrink-0">
                  <Mail size={18} className="text-[#1FA8FF]" />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-300">{t("common.email")}</div>
                  <a href="mailto:sales@push2stream.com" className="font-display text-base md:text-lg font-semibold text-white hover:text-[#1FA8FF] transition-colors break-all">
                    sales@push2stream.com
                  </a>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-xl glass flex items-center justify-center shrink-0">
                  <Phone size={18} className="text-[#1FA8FF]" />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-300">{t("common.phone")}</div>
                  <a href="tel:+12026561110" className="font-display text-base md:text-lg font-semibold text-white hover:text-[#1FA8FF] transition-colors">
                    +1 (202) 656-1110
                  </a>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-xl glass flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-[#1FA8FF]" />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-300">{t("common.headquarters")}</div>
                  <div className="font-display text-base md:text-lg font-semibold text-white">5850 Waterloo Rd</div>
                  <div className="text-sm text-slate-200">Columbia, MD 21045 — USA</div>
                </div>
              </div>
            </div>

            <div className="mt-10 md:mt-12 p-6 glass rounded-2xl">
              <div className="text-xs font-mono uppercase tracking-widest text-[#1FE0A8]">{p.contact_side_title}</div>
              <p className="mt-2 text-slate-100">{p.contact_side_sub}</p>
              <Link to="/demo" data-testid="contact-side-demo-link" className="btn-primary mt-5">
                {t("cta.book_demo")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="glass rounded-3xl p-6 sm:p-8 md:p-10">
              {sent ? (
                <div data-testid="contact-success" className="text-center py-10 md:py-12">
                  <div className="w-16 h-16 rounded-full gradient-bg mx-auto flex items-center justify-center text-black mb-6">
                    <Send size={26} />
                  </div>
                  <h3 className="font-display text-3xl font-bold tracking-tight text-white">{p.contact_success_title}</h3>
                  <p className="mt-3 text-slate-200">{p.contact_success_sub}</p>
                  <button onClick={() => setSent(false)} data-testid="contact-send-another" className="btn-ghost mt-8">
                    {p.contact_send_another}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="form-label">{t("common.full_name")} *</label>
                      <input data-testid="contact-name-input" required type="text" className="form-input" placeholder="Jane Cooper" value={form.name} onChange={onChange("name")} />
                    </div>
                    <div>
                      <label className="form-label">{t("common.work_email")} *</label>
                      <input data-testid="contact-email-input" required type="email" className="form-input" placeholder="jane@company.com" value={form.email} onChange={onChange("email")} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="form-label">{t("common.company")}</label>
                      <input data-testid="contact-company-input" type="text" className="form-input" placeholder="Acme Streaming" value={form.company} onChange={onChange("company")} />
                    </div>
                    <div>
                      <label className="form-label">{t("common.phone")}</label>
                      <input data-testid="contact-phone-input" type="tel" className="form-input" placeholder="+1 (555) 000 0000" value={form.phone} onChange={onChange("phone")} />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">{p.contact_form_message_label} *</label>
                    <textarea data-testid="contact-message-input" required className="form-input" rows={6} placeholder={p.contact_form_message_placeholder} value={form.message} onChange={onChange("message")} />
                  </div>
                  <button data-testid="contact-submit-btn" type="submit" disabled={loading} className="btn-primary w-full justify-center">
                    {loading ? t("common.sending") : <>{t("cta.send_message")} <ArrowRight size={18} /></>}
                  </button>
                  <p className="text-xs text-slate-300 text-center">{p.contact_disclaimer}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
